import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Code as CodeIcon,
  Cloud as CloudIcon,
  Devices as DevicesIcon,
  SmartToy as SmartToyIcon,
  Speed as SpeedIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { MetricCard, DataFlowDiagram, DataFlowNode } from '../../../../components/observatory';
import { 
  ApplicationObservatoryStatistics, 
  ApplicationObservatorySnapshot 
} from '../hooks/useApplicationObservatory';

interface StatisticsPanelProps {
  statistics: ApplicationObservatoryStatistics | null;
  snapshot: ApplicationObservatorySnapshot | null;
}

export const StatisticsPanel: React.FC<StatisticsPanelProps> = ({ statistics, snapshot }) => {
  if (!statistics) {
    return (
      <Card>
        <CardHeader title="System Statistics" />
        <CardContent>
          <Typography>Loading statistics...</Typography>
        </CardContent>
      </Card>
    );
  }

  const dataFlowNodes: DataFlowNode[] = [
    {
      id: 'apps',
      label: 'Apps',
      value: statistics.totalApplications,
      sublabel: 'Definitions',
      color: 'primary',
    },
    {
      id: 'functions',
      label: 'Functions',
      value: statistics.totalFunctions,
      sublabel: 'API Operations',
      color: 'secondary',
    },
    {
      id: 'clients',
      label: 'Clients',
      value: statistics.totalClients,
      sublabel: 'Consumers',
      color: 'info',
    },
    {
      id: 'calls',
      label: 'Calls/Day',
      value: statistics.apiCallsToday,
      sublabel: 'API Requests',
      color: 'success',
    },
  ];

  return (
    <Card>
      <CardHeader title="System Statistics" />
      <CardContent>
        {/* Core Metrics */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          Core Metrics
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<AppsIcon />}
              value={statistics.totalApplications}
              label="APPLICATIONS"
              color="primary"
              chip={{
                label: `${statistics.publishedApplications} published`,
                color: 'success',
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<CodeIcon />}
              value={statistics.totalFunctions}
              label="FUNCTIONS"
              color="secondary"
              chip={{
                label: `${statistics.implementedFunctions} implemented`,
                color: 'info',
                variant: 'outlined',
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<CloudIcon />}
              value={statistics.totalEnvironments}
              label="ENVIRONMENTS"
              color="info"
              chip={{
                label: `${statistics.healthyEnvironments} healthy`,
                color: 'success',
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<DevicesIcon />}
              value={statistics.totalClients}
              label="CLIENTS"
              color="success"
              sublabel="Agent + UI + API"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Client Breakdown */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          Client Distribution
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<SmartToyIcon />}
              value={statistics.agentClients}
              label="AGENT CLIENTS"
              color="primary"
              sublabel="AI & MCP Interfaces"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<DevicesIcon />}
              value={statistics.uiClients}
              label="UI CLIENTS"
              color="secondary"
              sublabel="Web, Mobile, Desktop"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<CodeIcon />}
              value={statistics.apiClients}
              label="API CLIENTS"
              color="info"
              sublabel="Direct API & Webhooks"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<SpeedIcon />}
              value={statistics.apiCallsThisHour}
              label="CALLS/HOUR"
              color="success"
              chip={{
                label: `${statistics.averageResponseTime}ms avg`,
                variant: 'outlined',
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Activity Metrics */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          Activity Metrics
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<TrendingUpIcon />}
              value={statistics.totalApiCalls.toLocaleString()}
              label="TOTAL API CALLS"
              color="primary"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<TrendingUpIcon />}
              value={statistics.apiCallsToday}
              label="CALLS TODAY"
              color="success"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<SpeedIcon />}
              value={`${statistics.averageResponseTime}ms`}
              label="AVG RESPONSE"
              color="info"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <MetricCard
              icon={<ErrorIcon />}
              value={`${statistics.errorRate}%`}
              label="ERROR RATE"
              color={statistics.errorRate > 1 ? 'error' : 'success'}
            />
          </Grid>
        </Grid>

        {/* Status Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {statistics.draftApplications > 0 && (
            <Chip
              icon={<AppsIcon />}
              label={`${statistics.draftApplications} Draft Apps`}
              color="warning"
              variant="outlined"
            />
          )}
          {statistics.rebuildRequired > 0 && (
            <Chip
              icon={<AppsIcon />}
              label={`${statistics.rebuildRequired} Need Rebuild`}
              color="error"
              variant="outlined"
            />
          )}
          {statistics.unimplementedFunctions > 0 && (
            <Chip
              icon={<CodeIcon />}
              label={`${statistics.unimplementedFunctions} Unimplemented`}
              color="warning"
              variant="outlined"
            />
          )}
          {statistics.degradedEnvironments > 0 && (
            <Chip
              icon={<CloudIcon />}
              label={`${statistics.degradedEnvironments} Degraded Envs`}
              color="error"
              variant="outlined"
            />
          )}
        </Box>

        {/* Data Flow */}
        <DataFlowDiagram
          nodes={dataFlowNodes}
          title="Data Flow"
        />

        {/* Top Performers */}
        {(statistics.mostActiveApplication || statistics.slowestFunction) && (
          <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Highlights
            </Typography>
            <Grid container spacing={2}>
              {statistics.mostActiveApplication && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.main' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                      🔥 Most Active Application
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {statistics.mostActiveApplication.name}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'success.dark', mt: 0.5 }}>
                      {statistics.mostActiveApplication.callCount.toLocaleString()} calls
                    </Typography>
                  </Box>
                </Grid>
              )}
              {statistics.slowestFunction && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1, border: 1, borderColor: 'warning.main' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark', mb: 1 }}>
                      ⏱️ Needs Optimization
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {statistics.slowestFunction.name}()
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'warning.dark', mt: 0.5 }}>
                      {(statistics.slowestFunction.avgDuration / 1000).toFixed(1)}s avg
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Real-time Snapshot */}
        {snapshot && (
          <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Real-time Activity</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`Active Sessions: ${snapshot.activeSessions.length}`} color="success" variant="outlined" />
              <Chip label={`Active Requests: ${snapshot.activeRequests}`} color="warning" variant="outlined" />
              <Chip label={`Connected Clients: ${snapshot.connectedClients}`} variant="outlined" />
            </Box>

            {snapshot.recentErrors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.875rem', color: 'error.main' }}>
                  Recent Errors ({snapshot.recentErrors.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {snapshot.recentErrors.slice(0, 3).map((error, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1,
                        bgcolor: 'error.50',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'error.light',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {new Date(error.timestamp).toLocaleTimeString()} - {error.functionName}
                      </Typography>
                      <Typography variant="body2" color="error.main">
                        {error.error}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatisticsPanel;

